import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ProviderBadges, Stars } from "@/components/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, Plus } from "lucide-react";

export default function Compare() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/providers/search?sort=recommended").then((r) => {
      setAll(r.data.providers);
      setSelected(r.data.providers.slice(0, 3));
    }).catch(() => {});
  }, []);

  const rows = [
    { label: "Rating", get: (p) => <Stars rating={p.rating} count={p.review_count} /> },
    { label: "Starting price", get: (p) => <span className="font-bold text-lawn">${Math.min(...(p.services?.map((s) => s.price) || [0]))}</span> },
    { label: "Reviews", get: (p) => p.review_count },
    { label: "City", get: (p) => p.city },
    { label: "Response time", get: (p) => `${p.response_time_hours}h` },
    { label: "Services offered", get: (p) => p.services?.length },
    { label: "Verified", get: (p) => p.verified ? <Check className="w-5 h-5 text-lawn" /> : <X className="w-4 h-4 text-gray-300" /> },
    { label: "Instant booking", get: (p) => p.instant_booking ? <Check className="w-5 h-5 text-lawn" /> : <X className="w-4 h-4 text-gray-300" /> },
    { label: "Recurring plans", get: () => <Check className="w-5 h-5 text-lawn" /> },
    { label: "Featured/Sponsored", get: (p) => <ProviderBadges badges={(p.badges || []).filter((b) => ["featured", "sponsored"].includes(b))} /> },
  ];

  const setSlot = (i, pid) => {
    const prov = all.find((x) => x.provider_id === pid);
    setSelected((prev) => prev.map((p, idx) => (idx === i ? prov : p)));
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-extrabold text-lawn-ink">Compare providers side by side</h1>
        <p className="text-muted-foreground mt-2">Pick up to 3 providers to compare price, rating, and features.</p>

        <div className="mt-8 overflow-x-auto">
          <table className="w-full border-collapse min-w-[640px]" data-testid="compare-table">
            <thead>
              <tr>
                <th className="w-40" />
                {selected.map((p, i) => (
                  <th key={i} className="p-4 align-top">
                    <div className="bg-white border border-border rounded-2xl p-4">
                      <img src={p?.profile_photo || p?.photos?.[0]} alt="" className="w-full h-24 object-cover rounded-xl mb-3" />
                      <Select value={p?.provider_id} onValueChange={(v) => setSlot(i, v)}>
                        <SelectTrigger data-testid={`compare-select-${i}`} className="rounded-xl text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{all.map((x) => <SelectItem key={x.provider_id} value={x.provider_id}>{x.business_name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button data-testid={`compare-book-${i}`} onClick={() => navigate(`/provider/${p?.provider_id}`)} className="w-full mt-3 rounded-full bg-lawn hover:bg-lawn-dark font-semibold text-sm h-9">View</Button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-white" : "bg-muted/40"}>
                  <td className="p-4 font-semibold text-sm text-lawn-ink">{row.label}</td>
                  {selected.map((p, i) => <td key={i} className="p-4 text-center text-sm">{p ? row.get(p) : "—"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
